import { useCallback, useRef } from 'react';
import type { OptionProp } from '../../shared/normalize-options';
import type { UseTypeaheadNavigationResult } from './use-typeahead-navigation';
import type { UseTypeaheadFilteringResult } from './use-typeahead-filtering';

type UseTypeaheadKeyboardParams = {
    navigation: UseTypeaheadNavigationResult;
    filtering: UseTypeaheadFilteringResult;
    commitOption: (option: OptionProp) => void;
    focusInput: () => void;
};

type UseTypeaheadKeyboardResult = {
    handleInputKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    handleOptionKeyDown: (option: OptionProp, index: number) => (event: React.KeyboardEvent<HTMLLIElement>) => void;
};

const TYPEAHEAD_RESET_TIMEOUT = 500;

const isPrintableCharacter = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key.length !== 1) return false;
    if (event.ctrlKey || event.metaKey || event.altKey) return false;
    return /\p{L}|\p{N}|\s/u.test(event.key);
};

const useTypeaheadKeyboard = ({ navigation, filtering, commitOption, focusInput }: UseTypeaheadKeyboardParams): UseTypeaheadKeyboardResult => {
    const typeaheadBufferRef = useRef('');
    const typeaheadTimeoutRef = useRef<number | undefined>();

    const resetTypeaheadBuffer = useCallback(() => {
        typeaheadBufferRef.current = '';
        if (typeaheadTimeoutRef.current) {
            window.clearTimeout(typeaheadTimeoutRef.current);
            typeaheadTimeoutRef.current = undefined;
        }
    }, []);

    const appendToTypeaheadBuffer = useCallback(
        (char: string) => {
            typeaheadBufferRef.current = typeaheadBufferRef.current + char;
            if (typeaheadTimeoutRef.current) {
                window.clearTimeout(typeaheadTimeoutRef.current);
            }
            typeaheadTimeoutRef.current = window.setTimeout(resetTypeaheadBuffer, TYPEAHEAD_RESET_TIMEOUT);
            return typeaheadBufferRef.current;
        },
        [resetTypeaheadBuffer]
    );

    const commitIfPossible = useCallback(
        (index: number) => {
            const option = filtering.flattenedVisibleOptions[index];
            if (!option || option.disabled) return;
            commitOption(option);
            focusInput();
        },
        [commitOption, filtering.flattenedVisibleOptions, focusInput]
    );

    const handleInputKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                resetTypeaheadBuffer();
                filtering.open();
                const nextIndex = navigation.getNextSelectableIndex(navigation.activeIndex);
                if (nextIndex !== -1) {
                    navigation.setActiveIndex(nextIndex);
                    navigation.requestOptionFocus(nextIndex);
                }
                return;
            }

            if (event.key === 'ArrowUp') {
                event.preventDefault();
                resetTypeaheadBuffer();
                filtering.open();
                const isAtTop = navigation.activeIndex === -1 || navigation.activeIndex === navigation.firstSelectableIndex || navigation.firstSelectableIndex === -1;
                if (isAtTop) {
                    navigation.setActiveIndex(-1);
                    navigation.requestOptionFocus(-1);
                    focusInput();
                    return;
                }
                const previousIndex = navigation.getPreviousSelectableIndex(navigation.activeIndex);
                if (previousIndex !== -1) {
                    navigation.setActiveIndex(previousIndex);
                    navigation.requestOptionFocus(previousIndex);
                }
                return;
            }

            if (event.key === 'Home') {
                event.preventDefault();
                resetTypeaheadBuffer();
                if (navigation.firstSelectableIndex !== -1) {
                    navigation.setActiveIndex(navigation.firstSelectableIndex);
                    navigation.requestOptionFocus(navigation.firstSelectableIndex);
                }
                return;
            }

            if (event.key === 'End') {
                event.preventDefault();
                resetTypeaheadBuffer();
                if (navigation.lastSelectableIndex !== -1) {
                    navigation.setActiveIndex(navigation.lastSelectableIndex);
                    navigation.requestOptionFocus(navigation.lastSelectableIndex);
                }
                return;
            }

            if (event.key === 'Tab' && filtering.isOpen && !event.shiftKey) {
                event.preventDefault();
                resetTypeaheadBuffer();
                const nextIndex = navigation.getNextSelectableIndex(navigation.activeIndex);
                if (nextIndex !== -1) {
                    navigation.setActiveIndex(nextIndex);
                    navigation.requestOptionFocus(nextIndex);
                }
                return;
            }

            if (event.key === 'Tab' && filtering.isOpen && event.shiftKey) {
                event.preventDefault();
                resetTypeaheadBuffer();
                const previousIndex = navigation.getPreviousSelectableIndex(navigation.activeIndex);
                if (previousIndex !== -1) {
                    navigation.setActiveIndex(previousIndex);
                    navigation.requestOptionFocus(previousIndex);
                }
                return;
            }

            if ((event.key === 'Enter' || event.key === ' ') && filtering.isOpen && navigation.activeIndex >= 0) {
                event.preventDefault();
                resetTypeaheadBuffer();
                commitIfPossible(navigation.activeIndex);
                return;
            }

            if (event.key === 'Escape') {
                filtering.close();
                resetTypeaheadBuffer();
                return;
            }

            if (event.key === 'Tab' && !event.shiftKey) {
                filtering.close();
                resetTypeaheadBuffer();
            }
        },
        [commitIfPossible, filtering, focusInput, navigation, resetTypeaheadBuffer]
    );

    const findMatchIndex = useCallback(
        (startIndex: number, query: string) => {
            if (!query) return -1;
            const lowered = query.toLowerCase();
            const options = filtering.flattenedVisibleOptions;
            if (!options.length) return -1;

            const start = startIndex >= 0 ? (startIndex + 1) % options.length : 0;
            for (let offset = 0; offset < options.length; offset++) {
                const candidateIndex = (start + offset) % options.length;
                const candidate = options[candidateIndex];
                if (!candidate.disabled && (candidate.label.toLowerCase().startsWith(lowered) || candidate.value.toLowerCase().startsWith(lowered))) {
                    return candidateIndex;
                }
            }
            return -1;
        },
        [filtering.flattenedVisibleOptions]
    );

    const handleOptionKeyDown = useCallback(
        (option: OptionProp, index: number) => (event: React.KeyboardEvent<HTMLLIElement>) => {
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                resetTypeaheadBuffer();
                const nextIndex = navigation.getNextSelectableIndex(index);
                if (nextIndex !== -1) {
                    navigation.setActiveIndex(nextIndex);
                    navigation.requestOptionFocus(nextIndex);
                }
                return;
            }

            if (event.key === 'ArrowUp') {
                event.preventDefault();
                resetTypeaheadBuffer();
                if (index === navigation.firstSelectableIndex || navigation.firstSelectableIndex === -1) {
                    navigation.setActiveIndex(-1);
                    navigation.requestOptionFocus(-1);
                    focusInput();
                    return;
                }
                const previousIndex = navigation.getPreviousSelectableIndex(index);
                if (previousIndex !== -1) {
                    navigation.setActiveIndex(previousIndex);
                    navigation.requestOptionFocus(previousIndex);
                }
                return;
            }

            if (event.key === 'Home') {
                event.preventDefault();
                resetTypeaheadBuffer();
                if (navigation.firstSelectableIndex !== -1) {
                    navigation.setActiveIndex(navigation.firstSelectableIndex);
                    navigation.requestOptionFocus(navigation.firstSelectableIndex);
                }
                return;
            }

            if (event.key === 'End') {
                event.preventDefault();
                resetTypeaheadBuffer();
                if (navigation.lastSelectableIndex !== -1) {
                    navigation.setActiveIndex(navigation.lastSelectableIndex);
                    navigation.requestOptionFocus(navigation.lastSelectableIndex);
                }
                return;
            }

            if (event.key === 'Tab' && !event.shiftKey) {
                event.preventDefault();
                resetTypeaheadBuffer();
                const nextIndex = navigation.getNextSelectableIndex(index);
                if (nextIndex !== -1) {
                    navigation.setActiveIndex(nextIndex);
                    navigation.requestOptionFocus(nextIndex);
                }
                return;
            }

            if (event.key === 'Tab' && event.shiftKey) {
                event.preventDefault();
                resetTypeaheadBuffer();
                const previousIndex = navigation.getPreviousSelectableIndex(index);
                if (previousIndex !== -1) {
                    navigation.setActiveIndex(previousIndex);
                    navigation.requestOptionFocus(previousIndex);
                }
                return;
            }

            if (event.key === 'Backspace') {
                event.preventDefault();
                navigation.setActiveIndex(-1);
                navigation.requestOptionFocus(-1);
                resetTypeaheadBuffer();
                focusInput();
                return;
            }

            if (event.key === 'Escape') {
                event.preventDefault();
                navigation.setActiveIndex(-1);
                navigation.requestOptionFocus(-1);
                resetTypeaheadBuffer();
                filtering.close();
                focusInput();
                return;
            }

            if ((event.key === 'Enter' || event.key === ' ') && !option.disabled) {
                event.preventDefault();
                commitOption(option);
                focusInput();
                return;
            }

            if (isPrintableCharacter(event)) {
                event.preventDefault();
                const buffer = appendToTypeaheadBuffer(event.key);
                const matchIndex = findMatchIndex(index, buffer);
                if (matchIndex !== -1) {
                    navigation.setActiveIndex(matchIndex);
                    navigation.requestOptionFocus(matchIndex);
                    return;
                }

                resetTypeaheadBuffer();
                const fallbackIndex = findMatchIndex(-1, event.key);
                if (fallbackIndex !== -1) {
                    navigation.setActiveIndex(fallbackIndex);
                    navigation.requestOptionFocus(fallbackIndex);
                }
                return;
            }
        },
        [appendToTypeaheadBuffer, commitOption, findMatchIndex, focusInput, navigation, resetTypeaheadBuffer]
    );

    return { handleInputKeyDown, handleOptionKeyDown };
};

export type { UseTypeaheadKeyboardParams, UseTypeaheadKeyboardResult };
export { useTypeaheadKeyboard };
